import _ from 'lodash'

/**
 * react-hook-form often omits unchecked checkboxes from submitted values (undefined).
 */
export function formCheckboxChecked(values: Record<string, unknown>, fieldId: string): boolean {
  const v = values[fieldId]
  return v === true || v === 'on' || v === 'true'
}

export function toolPermissionDisplayName(id: string | undefined | null): string {
  if (!id || typeof id !== 'string') return String(id ?? '')
  return id.startsWith('tool:') ? id.slice('tool:'.length) : id
}

export function toolPermissionSubtitle(row: { id?: string; label?: string; description?: string }): string {
  const name = toolPermissionDisplayName(row.id)
  const desc = (row.description || '').trim()
  const lbl = (row.label || '').trim()
  if (desc && desc !== name) return desc
  if (lbl && lbl !== name) return lbl
  return ''
}

export function mergeToolPermissions(
  canonicalTools: Array<Record<string, unknown>>,
  accessPermission: Array<Record<string, unknown>> | null | undefined
) {
  const savedToolRows = (accessPermission || []).filter((p) => p && p.type === 'mcp_tool')
  const savedById = _.keyBy(savedToolRows, 'id')
  const list = Array.isArray(canonicalTools) ? canonicalTools : []
  const seen = new Set<string>()

  const fromApi = list.map((t) => {
    const id = String(t.id || '')
    seen.add(id)
    const saved = savedById[id]
    const category = (t.category as string) || 'other'
    const isLockedPayhelm = category === 'payhelm'
    return {
      id,
      label: (t.label as string) || id,
      description: (t.description as string) || '',
      type: 'mcp_tool',
      category,
      categoryLabel: (t.categoryLabel as string) || 'Other',
      checked: isLockedPayhelm ? true : saved !== undefined ? !!saved.checked : true,
    }
  })

  const orphanSaved = savedToolRows
    .filter((s) => !seen.has(String(s.id)))
    .map((s) => {
      const category = (s.category as string) || 'other'
      const isLockedPayhelm = category === 'payhelm'
      return {
        ...s,
        label: (s.label as string) || String(s.id),
        description: (s.description as string) || '',
        type: 'mcp_tool',
        category,
        categoryLabel: (s.categoryLabel as string) || 'Other',
        checked: isLockedPayhelm ? true : !!s.checked,
      }
    })

  return [...fromApi, ...orphanSaved]
}

export function isPayhelmLockedCategory(categoryKey: string | undefined): boolean {
  return categoryKey === 'payhelm'
}

export function toolRowMatchesSearch(
  row: { id?: string; label?: string; description?: string; categoryLabel?: string },
  queryRaw: string
): boolean {
  const q = (queryRaw || '').trim().toLowerCase()
  if (!q) return true
  const title = toolPermissionDisplayName(row.id).toLowerCase()
  const subtitle = toolPermissionSubtitle(row).toLowerCase()
  const id = String(row.id || '').toLowerCase()
  const label = String(row.label || '').toLowerCase()
  const desc = String(row.description || '').toLowerCase()
  const catLabel = String(row.categoryLabel || '').toLowerCase()
  return (
    title.includes(q) ||
    subtitle.includes(q) ||
    id.includes(q) ||
    label.includes(q) ||
    desc.includes(q) ||
    catLabel.includes(q)
  )
}

/** Toggleable MCP tools for "X of Y" column (excludes locked payhelm category). */
export function countToggleableToolAccess(
  accessPermission: Array<Record<string, unknown>> | null | undefined,
  canonicalTools: Array<Record<string, unknown>> | null | undefined
): { enabled: number; total: number } {
  const merged = mergeToolPermissions(canonicalTools || [], accessPermission)
  const toggleable = merged.filter((r) => !isPayhelmLockedCategory(r.category as string))
  const total = toggleable.length
  const enabled = toggleable.filter((r) => r.checked).length
  return { enabled, total }
}
